import type { User, UserRole } from "../data/schemaTypes";

// validateCredentials — POST /api/auth/login.
//
// El backend valida bcrypt y devuelve user info. Si las credenciales son
// inválidas responde 401 con un body `{"error":"..."}`. Cualquier otro
// código de status (500/503/network down) se reporta como error genérico.

export type LoginResult =
  | { ok: true; user: User }
  | { ok: false; reason: string };

type LoginResponse = {
  user_id: string;
  account_id: string;
  role: string;
  email: string;
  full_name: string;
  code?: string;
  phone?: string;
};

const VALID_ROLES: UserRole[] = ["admin", "coordinator", "supervisor", "driver"];

export async function validateCredentials(
  email: string,
  password: string,
): Promise<LoginResult> {
  try {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (res.status === 401) {
      const body = await res.json().catch(() => ({}));
      return {
        ok: false,
        reason:
          (body as { error?: string }).error ?? "Credenciales inválidas",
      };
    }
    if (!res.ok) {
      return { ok: false, reason: `Error del servidor (${res.status})` };
    }

    const data = (await res.json()) as LoginResponse;
    const role = data.role as UserRole;
    if (!VALID_ROLES.includes(role)) {
      return { ok: false, reason: `Rol no soportado: ${data.role}` };
    }

    const user: User = {
      id: data.user_id,
      account_id: data.account_id,
      role,
      email: data.email,
      full_name: data.full_name,
      phone: data.phone,
      code: data.code,
      active: true, // si el backend autenticó al user, está activo
    };
    return { ok: true, user };
  } catch (e) {
    return {
      ok: false,
      reason: `No se pudo conectar con el servidor: ${(e as Error).message}`,
    };
  }
}
